import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './WareEffort.css';
import { useTranslation } from 'react-i18next';
import WarEffortSpoilsDialog from './WarEffortSpoilsDialog';
import WarEffortPurposeSelection from './WarEffortPurposeSelection';
import WarEffortFortification from './WarEffortFortification';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { API_URL } from './config/apiConfig';
import { WorldMap, getAllMappedCellIds } from './worldMap';

function WareEffort({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  
  // Play war effort music
  useMusic('/music/war_effort.mp3');
  
  const [currentMission, setCurrentMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leavingMission, setLeavingMission] = useState(false);
  const [missionJustLeft, setMissionJustLeft] = useState(false);
  const [showSpoilsDialog, setShowSpoilsDialog] = useState(false);
  const [hasUnclaimedSpoils, setHasUnclaimedSpoils] = useState(false);
  const [warEffortData, setWarEffortData] = useState(null);
  const missionJustLeftRef = useRef(false);
  const inPurposeSelectionRef = useRef(false);
  
  // Check for unclaimed spoils
  const checkUnclaimedSpoils = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/spoils`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const spoils = await response.json();
        setHasUnclaimedSpoils(spoils.length > 0);
      }
    } catch (error) {
      console.error('Error checking spoils:', error);
    }
  }, []);
  
  // Fetch active mission for user
  const fetchActiveMission = useCallback(async (force = false) => {
    // Don't fetch if we just left a mission (prevent race condition)
    // Use ref to avoid stale closure issues
    if (missionJustLeftRef.current || missionJustLeft) {
      console.log('Skipping fetchActiveMission - mission was just left');
      return;
    }
    
    // Don't fetch if we're in purpose selection mode (currentMission is null)
    // unless we're forcing a fetch (e.g., on initial mount)
    // This prevents unnecessary fetches when user is selecting a purpose
    // Use ref to check this to avoid stale closures
    if (!force && (currentMission === null || inPurposeSelectionRef.current)) {
      console.log('Skipping fetchActiveMission - in purpose selection mode', { 
        currentMission, 
        inPurposeSelection: inPurposeSelectionRef.current 
      });
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to fetch active mission
      const missionResponse = await fetch(`${API_URL}/war-effort/active-mission`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (missionResponse.ok) {
        const mission = await missionResponse.json();
        // Double-check that mission status is not 'abandoned' (backend should filter this, but verify)
        if (mission.status === 'abandoned') {
          console.log('Mission is abandoned, not setting as current mission');
          setCurrentMission(null);
        } else {
          // Verify that the current user is actually part of this mission
          // Get userId from JWT token (more reliable than localStorage, especially for private windows)
          let userIdNum = null;
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const decoded = jwtDecode(token);
              userIdNum = decoded.userId ? Number(decoded.userId) : null;
            }
          } catch (error) {
            console.error('Error decoding token:', error);
            // Fallback to localStorage
            const userId = localStorage.getItem('userId');
            userIdNum = userId ? Number(userId) : null;
          }
          
          const player1Id = mission.player1_id ? Number(mission.player1_id) : null;
          const player2Id = mission.player2_id ? Number(mission.player2_id) : null;
          
          if (userIdNum && (userIdNum === player1Id || userIdNum === player2Id)) {
            setCurrentMission(mission);
          } else {
            console.warn('Current user is not part of this mission, ignoring it', {
              userId: userIdNum,
              player1Id,
              player2Id,
              missionId: mission.id
            });
            setCurrentMission(null);
          }
        }
      } else if (missionResponse.status === 404) {
        // No active mission found - show purpose selection
        setCurrentMission(null);
      } else {
        console.error('Error fetching active mission:', missionResponse.status);
        setCurrentMission(null);
      }
      
      // Also fetch war effort status for other data
      const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        setWarEffortData(data);
      }
    } catch (error) {
      console.error('Error fetching war effort data:', error);
      setCurrentMission(null);
    } finally {
      setLoading(false);
    }
  }, [missionJustLeft, currentMission]);
  
  useEffect(() => {
    // Only fetch on mount if we haven't just left a mission and not in purpose selection
    // Force fetch on initial mount to check for existing mission
    if (!missionJustLeft && !missionJustLeftRef.current && !inPurposeSelectionRef.current) {
      fetchActiveMission(true); // Force fetch on mount
    }
    checkUnclaimedSpoils();
    
    // Only set up polling if we have an active mission
    // If we're in purpose selection mode, don't poll
    const interval = setInterval(() => {
      // Don't poll if we just left a mission or in purpose selection
      if (missionJustLeftRef.current || missionJustLeft || inPurposeSelectionRef.current) {
        console.log('Skipping poll - mission just left or in purpose selection');
        return;
      }
      // Only poll if we have an active mission (not in purpose selection)
      // This prevents unnecessary API calls when user is selecting a purpose
      if (currentMission !== null) {
        fetchActiveMission(false);
      }
      checkUnclaimedSpoils();
    }, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchActiveMission, checkUnclaimedSpoils, missionJustLeft]);
  
  const handlePurposeSelected = (mission) => {
    // We're leaving purpose selection mode, clear the flag
    inPurposeSelectionRef.current = false;
    setCurrentMission(mission);
  };
  
  const handleMissionUpdate = (mission) => {
    // Mission update means we have an active mission, not in purpose selection
    inPurposeSelectionRef.current = false;
    setCurrentMission(mission);
  };
  
  const handleLeaveMission = async () => {
    if (!window.confirm('Are you sure you want to leave this mission? This will abandon your current progress.')) {
      return;
    }
    
    if (leavingMission) {
      return; // Prevent double-clicks
    }
    
    try {
      setLeavingMission(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('You must be logged in to leave a mission.');
        setLeavingMission(false);
        return;
      }
      
      console.log('Leaving mission...');
      const response = await fetch(`${API_URL}/war-effort/leave-mission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        console.log('Mission left successfully:', responseData);
        // Set flag to prevent polling from re-fetching mission
        // Use both state and ref to ensure we catch all cases
        missionJustLeftRef.current = true;
        setMissionJustLeft(true);
        // Mark that we're now in purpose selection mode
        inPurposeSelectionRef.current = true;
        // Clear current mission immediately - this will show purpose selection
        // Use a function form to ensure we're clearing the state
        setCurrentMission(prev => {
          console.log('Clearing current mission, previous value:', prev);
          return null;
        });
        // Refresh war effort status data (but not mission - we just left it)
        // Do this in the background without blocking
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/war-effort/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(statusResponse => {
            if (statusResponse.ok) {
              return statusResponse.json();
            }
          })
          .then(data => {
            if (data) {
              setWarEffortData(data);
            }
          })
          .catch(error => {
            console.error('Error refreshing war effort status:', error);
          });
        // Clear the flag after a delay to allow backend update to propagate
        // Also verify the mission is actually gone before clearing the flag
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            const verifyResponse = await fetch(`${API_URL}/war-effort/active-mission`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (verifyResponse.status === 404) {
              // Mission is confirmed gone, safe to resume polling
              console.log('Mission confirmed abandoned, resuming polling');
              missionJustLeftRef.current = false;
              setMissionJustLeft(false);
              // Keep inPurposeSelectionRef true - we're in purpose selection mode
              // This will be cleared when user selects a new purpose
            } else {
              // Mission still exists, keep flag set for longer
              console.log('Mission still exists, extending flag duration');
              setTimeout(() => {
                missionJustLeftRef.current = false;
                setMissionJustLeft(false);
                // Keep inPurposeSelectionRef true
              }, 5000); // Another 5 seconds
            }
          } catch (error) {
            console.error('Error verifying mission status:', error);
            // On error, clear flag anyway after delay
            setTimeout(() => {
              missionJustLeftRef.current = false;
              setMissionJustLeft(false);
              // Keep inPurposeSelectionRef true
            }, 5000);
          }
        }, 2000); // Check after 2 seconds
        // Small delay to ensure state update propagates before showing alert
        setTimeout(() => {
          alert('Mission left successfully. You can now start a new mission.');
        }, 100);
      } else {
        console.error('Failed to leave mission:', responseData);
        alert(`Failed to leave mission: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error leaving mission:', error);
      alert(`Failed to leave mission: ${error.message || 'Please try again.'}`);
    } finally {
      setLeavingMission(false);
    }
  };
  
  // Get faction background for world map
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };
  
  const getFactionBackground = (faction) => {
    if (!faction) return '#808080';
    const color = factionColors[faction];
    if (!color) return '#808080';
    
    // Convert hex to rgba
    const hexToRgba = (hex, alpha = 0.25) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    if (color.includes('gradient')) {
      return color.replace(/#([0-9A-Fa-f]{6})/g, (match, hex) => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 1.0)`;
      });
    } else {
      return hexToRgba(color, 1.0);
    }
  };
  
  // Create cell owners map from war effort data
  const cellOwnersMap = useMemo(() => {
    if (!warEffortData?.cells) return {};
    const map = {};
    warEffortData.cells.forEach(cell => {
      if (cell.owner_name) {
        map[String(cell.cell_id)] = cell.owner_name;
      }
    });
    return map;
  }, [warEffortData]);
  
  const beaconCells = useMemo(() => {
    if (!warEffortData?.cellsWithWaitingPlayers) return [];
    return warEffortData.cellsWithWaitingPlayers.map(Number);
  }, [warEffortData]);
  
  return (
    <div className="wareffort-container">
      {/* Standard header with Friends, Settings, User name */}
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
        <FriendsIcon />
        <SettingsMenu onLogout={() => {
          onLogout();
          navigate('/');
        }} />
        <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
      </div>

      {/* Back button */}
      <button onClick={() => navigate('/battleoverview')} className="back-button">
        {t('common.back')}
      </button>
      
      {/* Leave Mission button (if active mission exists) */}
      {currentMission && (
        <button
          onClick={handleLeaveMission}
          disabled={leavingMission}
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            zIndex: 1000,
            backgroundColor: leavingMission ? '#555' : '#8B0000',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '10px 20px',
            cursor: leavingMission ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
            opacity: leavingMission ? 0.6 : 1
          }}
        >
          {leavingMission ? 'Leaving...' : 'Leave Mission'}
        </button>
      )}
      
      {/* Claim Spoils button (if available) */}
      {hasUnclaimedSpoils && (
        <button
          className="spoils-button"
          onClick={() => setShowSpoilsDialog(true)}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000
          }}
        >
          Claim Spoils
        </button>
      )}
      
      {loading && !currentMission ? (
        <div className="wareffort-loading" style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          color: 'white', 
          fontSize: '20px',
          zIndex: 1000
        }}>
          Loading...
        </div>
      ) : !currentMission ? (
        <WarEffortPurposeSelection
          user={user}
          onPurposeSelected={handlePurposeSelected}
        />
      ) : (
        <WarEffortFortification
          mission={currentMission}
          user={user}
          onBack={() => setCurrentMission(null)}
          onFortificationComplete={handleMissionUpdate}
        />
      )}
      
      {/* Spoils Dialog */}
      <WarEffortSpoilsDialog
        isOpen={showSpoilsDialog}
        onClose={() => {
          setShowSpoilsDialog(false);
          checkUnclaimedSpoils();
        }}
        userId={typeof user === 'string' ? user : (user?.id || localStorage.getItem('userId'))}
        previewMode={false}
      />
    </div>
  );
}

export default WareEffort;
